import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import AsyncPaginate from 'react-select-async-paginate';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import InputLabel from '@material-ui/core/InputLabel';

const styles = theme => ({
  root: {
    flexGrow: 1,
  },
  input: {
    display: 'flex',
    height: "100%",
    padding: 0,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: `${theme.spacing(1) / 2}px ${theme.spacing(1) / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)}px ${theme.spacing(1) * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing(1) * 2,
  },
  label: {
    fontSize: 12
  }
});

const NoOptionsMessage = (props) => {
  const { selectProps, innerProps, children } = props;
  return (
    <Typography
      color="textSecondary"
      className={selectProps.classes.noOptionsMessage}
      {...innerProps}
    >
      {children}
    </Typography>
  );
};

const inputComponent = ({ inputRef, ...props }) => {
  return <div ref={inputRef} {...props} />;
};

const Control = (props) => {
  const {
    selectProps, innerRef, innerProps, children,
  } = props;
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: selectProps.classes.input,
          inputRef: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...selectProps.textFieldProps}
    />
  );
};

const Option = (props) => {
  const {
    innerRef, isFocused, innerProps, children, isSelected,
  } = props;
  return (
    <MenuItem
      buttonRef={innerRef}
      selected={isFocused}
      component="div"
      style={{
        fontWeight: isSelected ? 500 : 400,
      }}
      {...innerProps}
    >
      {children}
    </MenuItem>
  );
};

const Placeholder = (props) => {
  const { selectProps, innerProps, children } = props;
  return (
    <Typography
      color="textSecondary"
      className={selectProps.classes.placeholder}
      {...innerProps}
    >
      {children}
    </Typography>
  );
};

const SingleValue = (props) => {
  const { selectProps, innerProps, children } = props;
  return (
    <Typography className={selectProps.classes.singleValue} {...innerProps}>
      {children}
    </Typography>
  );
};

const ValueContainer = (props) => {
  const { selectProps, children } = props;
  return <div className={selectProps.classes.valueContainer}>{children}</div>;
};

const MultiValue = (props) => {
  const {
    selectProps, removeProps, isFocused, children,
  } = props;
  return (
    <Chip
      tabIndex={-1}
      label={children}
      className={classNames(selectProps.classes.chip, {
        [selectProps.classes.chipFocused]: isFocused,
      })}
      onDelete={removeProps.onClick}
      deleteIcon={<CancelIcon {...removeProps} />}
    />
  );
};

const Menu = (props) => {
  const { selectProps, innerProps, children } = props;
  return (
    <Paper square className={selectProps.classes.paper} {...innerProps}>
      {children}
    </Paper>
  );
};

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

class SingleSelect extends React.Component {
  handleChange = name => (value) => {
    const { handleUpdate } = this.props;
    handleUpdate(value);
  };

  render() {
    const {
      classes, theme, suggestions, suggestionTotal, loadMoreSuggestions, paginationEnabled, selected, placeholder, disabled, ...others
    } = this.props;

    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
    };

    const loadOptions = (search, loadedOptions) => {
      const defaultLimit = 10;
      const hasMore = paginationEnabled ? suggestionTotal > suggestions.length : false;
      if (hasMore) {
        loadMoreSuggestions(defaultLimit, loadedOptions.length + defaultLimit);
      }
      let newSuggestions = [...suggestions];
      if (loadedOptions.length > 1) {
        newSuggestions = newSuggestions.splice(loadedOptions.length);
      }
      return {
        options: newSuggestions,
        hasMore
      };
    };

    return (
      <div className={classes.root}>
        {this.props.label && <InputLabel className={classes.label}>{this.props.label}</InputLabel>}
        <AsyncPaginate
          value={selected}
          disabled={disabled}
          classes={classes}
          styles={selectStyles}
          textFieldProps={{
            InputLabelProps: {
              shrink: true,
            },
          }}
          loadOptions={loadOptions}
          closeOnMenuSelect={false}
          placeholder={placeholder}
          components={components}
          onChange={this.handleChange('')}
          {...others}
        />
      </div>
    );
  }
}

SingleSelect.defaultProps = {
  placeholder: 'Select One',
  disabled: false,
};

SingleSelect.propTypes = {
  selected: PropTypes.instanceOf(Object).isRequired,
  suggestions: PropTypes.instanceOf(Object).isRequired,
  handleUpdate: PropTypes.instanceOf(Function).isRequired,
  classes: PropTypes.instanceOf(Object).isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string
};

export default withStyles(styles, { withTheme: true })(SingleSelect);
